import { ReactElement, useState } from 'react';
import * as S from './LandmarkMainPage.styles';
import Layout from '@/shared/Layout/ui/Layout';
import { Container } from '@/common/ui/containers/Container';
import Subscribe from '@/shared/Subscribe/ui/Subscribe';
import { useMedia } from '@/common/service/hooks/useMedia';
import { TLandmarkDestination } from '../domain/Landmarks.domain';
import { DropdownArrow } from '@/shared/images/icons/DropdownArrow';
import { LandmarkCard } from './LandmarkCard';
import { CommonMeta } from '@/common/ui/meta/CommonMeta';
import { buildRootUrl } from '@/common/app/utils/urlsUtils';
import Landmark from '../../../shared/images/HeroImages/Landmark.jpg';
import { CardViewMore } from '@/screens/CommonPage/ui/commonPage/CardViewMore/CardViewMore';
import { ICommoDestination } from '@/screens/CommonPage/domain/CommonPage.domain';
import { Button } from '@/common/ui';
import Link from 'next/link';
import { AnswersFAQ } from '@/screens/ActivityPage/ui/Answers/AnswersFAQ';

const TITLE = `TripShock's Landmark Guides`;
const DESCRIPTION = `From historical hot spots to spectacular sightseeing, TripShock's Landmark Guides cover the places you don't want to miss. Browse our destinations to find the top landmarks in each location.`;
const CARDS_LIMIT_DESKTOP = 6;
const CARDS_LIMIT_MOBILE = 2;

export type LandmarkMainPageProps = {
    destinations: TLandmarkDestination[];
};

export const LandmarkMainPage = ({ destinations }: LandmarkMainPageProps): ReactElement => {
    const isMobileView = useMedia('(max-width: 991px)');
    const CARDS_LIMIT = isMobileView ? CARDS_LIMIT_MOBILE : CARDS_LIMIT_DESKTOP;
    const [indexItemOpen, setIndexItemOpen] = useState<number | undefined>(0);

    const openItem = (index: number) => {
        if (isMobileView && indexItemOpen === index) {
            setIndexItemOpen(undefined);
            return;
        }

        setIndexItemOpen(index);
    };

    const selectedDestination =
        typeof indexItemOpen !== 'undefined' ? destinations[indexItemOpen] : undefined;
    const curDestination = selectedDestination && selectedDestination.destination;
    const destinationPagePath = curDestination
        ? `/destination/${curDestination.state.code}/${curDestination.slug}/landmarks/`.toLowerCase()
        : '/';

    const cardsList = (
        <>
            {selectedDestination &&
                curDestination &&
                selectedDestination.items
                    .slice(0, CARDS_LIMIT)
                    .map((item) => (
                        <LandmarkCard
                            item={item}
                            destinationSlug={curDestination.slug}
                            stateCode={curDestination.state.code}
                            key={item.slug}
                            isTopSign
                        />
                    ))}
        </>
    );

    const canonical = buildRootUrl('/landmarks/');

    return (
        <Layout withMobileHeroImage noSearch>
            <CommonMeta
                title={TITLE}
                description={DESCRIPTION}
                slug=""
                ogUrl={canonical}
                ogType="article"
                ogTitle="Tripshock! Landmarks & Guides"
                ogImageAlt="Tripshock! Landmarks & Guides"
                imageSrc={Landmark.src}
                canonical={canonical}
                structuredData={[]}
            />
            <Container>
                <S.ParagraphWrapper>
                    <S.Title>{TITLE}</S.Title>
                    <S.Description>{DESCRIPTION}</S.Description>
                </S.ParagraphWrapper>
                <S.CommonWrapper data-test-id="Common_wrapper">
                    <S.ListContainer>
                        <S.ScrollListContainer>
                            {destinations.map(({ destination }, index) => (
                                <div key={destination.slug}>
                                    <S.MenuSelect
                                        key={destination.id}
                                        onClick={() => openItem(index)}
                                        isOpen={indexItemOpen === index}
                                    >
                                        <S.MenuHeader isOpen={indexItemOpen === index}>
                                            <S.DestinationName>
                                                {destination.name}
                                            </S.DestinationName>
                                            <S.CodeTitle>{destination?.state?.code}</S.CodeTitle>
                                            <S.CommonDropdown isOpen={indexItemOpen === index}>
                                                <DropdownArrow />
                                            </S.CommonDropdown>
                                        </S.MenuHeader>
                                        <S.MobileItemsContainer isOpen={indexItemOpen === index}>
                                            {index === indexItemOpen && (
                                                <>
                                                    {cardsList}
                                                    <Link href={destinationPagePath} passHref>
                                                        <Button
                                                            variant="primary"
                                                            onClick={() => {
                                                                return;
                                                            }}
                                                        >
                                                            All {curDestination?.name || ''}{' '}
                                                            Landmarks
                                                        </Button>
                                                    </Link>
                                                </>
                                            )}
                                        </S.MobileItemsContainer>
                                    </S.MenuSelect>
                                </div>
                            ))}
                        </S.ScrollListContainer>
                    </S.ListContainer>
                    <S.DesktopResultWrapper>
                        <S.ItemContainer>
                            {!!selectedDestination && (
                                <CardViewMore
                                    destination={
                                        selectedDestination.destination as ICommoDestination
                                    }
                                    type="landmarks"
                                />
                            )}
                            <S.DesktopResultContainer>{cardsList}</S.DesktopResultContainer>
                        </S.ItemContainer>
                    </S.DesktopResultWrapper>
                </S.CommonWrapper>
            </Container>
            <S.WrapperSubscribe>
                <Subscribe />
            </S.WrapperSubscribe>
            <AnswersFAQ title={TITLE} url={canonical} image={Landmark.src} id="landmarks" />
        </Layout>
    );
};
